/**
 * Bridges Clerk session tokens to non-React code (API services, SSE).
 *
 * clerk-js attaches a global `window.Clerk` instance once <ClerkProvider> mounts.
 * We use it to fetch a fresh session JWT on demand and mirror it into
 * localStorage("access_token") so existing call sites that read the token
 * synchronously (e.g. EventSource URLs) keep working.
 */

const ACCESS_TOKEN_KEY = "access_token";

declare global {
  interface Window {
    Clerk?: {
      session?: {
        getToken: (options?: { template?: string }) => Promise<string | null>;
      } | null;
    };
  }
}

/** Fetch a fresh Clerk session token and cache it. Returns null if signed out. */
export async function fetchClerkToken(): Promise<string | null> {
  try {
    const token = (await window.Clerk?.session?.getToken?.()) ?? null;
    if (token) {
      localStorage.setItem(ACCESS_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
    }
    return token;
  } catch {
    return null;
  }
}

/** Last token mirrored to localStorage (sync access for SSE / legacy callers). */
export function getCachedToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function clearCachedToken(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
}

/** Async token getter used by API services: prefers a fresh token, falls back to cache. */
export async function getAuthToken(): Promise<string | null> {
  if (window.Clerk?.session) {
    return fetchClerkToken();
  }
  return getCachedToken();
}
